/** Lossless container metadata cleaning. Image samples, ICC profiles and orientation stay intact. */
const invalid=()=>new Error('无法读取图片，请重新选择');
const ascii=(b:Uint8Array,p:number,n:number)=>String.fromCharCode(...b.subarray(p,p+n));
const join=(parts:Uint8Array[])=>{const b=new Uint8Array(parts.reduce((n,p)=>n+p.length,0));let n=0;for(const p of parts){b.set(p,n);n+=p.length;}return b;};
function orientation(b:Uint8Array):number|undefined {
    const base=ascii(b,0,6)==='Exif\0\0'?6:0;
    if(b.length<base+8)return;
    const little=ascii(b,base,2)==='II';if(!little&&ascii(b,base,2)!=='MM')return;
    const v=new DataView(b.buffer,b.byteOffset,b.byteLength);
    const u16=(p:number)=>v.getUint16(p,little),u32=(p:number)=>v.getUint32(p,little);
    if(u16(base+2)!==42)return;
    const ifd=base+u32(base+4);if(ifd<base+8||ifd+2>b.length)return;
    const count=u16(ifd);if(count>Math.floor((b.length-ifd-2)/12))return;
    for(let p=ifd+2;p<ifd+2+count*12;p+=12){if(u16(p)===0x112&&u16(p+2)===3&&u32(p+4)===1){const n=u16(p+8);if(n>=1&&n<=8)return n;}}
}
function cleanExif(b:Uint8Array,prefix:boolean):Uint8Array|null {
    const n=orientation(b);if(n===undefined)return null;
    const tiff=new Uint8Array([73,73,42,0,8,0,0,0,1,0,18,1,3,0,1,0,0,0,n,0,0,0,0,0,0,0]);
    return prefix?join([new Uint8Array([69,120,105,102,0,0]),tiff]):tiff;
}
function jpeg(b:Uint8Array):Uint8Array {
    const out=b.slice();let p=2,inside=true,ended=false;
    while(p<b.length){
        if(!inside){
            // Preserve concatenated MPF/gain-map JPEG offsets; erase non-image trailers.
            while(p<b.length&&!(b[p]===255&&b[p+1]===216)){out[p++]=0;}
            if(p>=b.length)break;p+=2;inside=true;
        }
        if(b[p]!==255)throw invalid();
        while(b[p]===255)p++;
        if(p>=b.length)throw invalid();
        const markerAt=p,marker=b[p++];
        if(marker===217){inside=false;ended=true;continue;}
        if(marker===1||(marker>=208&&marker<=215))continue;
        if(p+2>b.length)throw invalid();
        const length=b[p]*256+b[p+1],start=p+2,end=p+length;
        if(length<2||end>b.length)throw invalid();
        const app=marker>=224&&marker<=239;
        const keep=(marker===224&&ascii(b,start,5)==='JFIF\0')||
            (marker===226&&(ascii(b,start,12)==='ICC_PROFILE\0'||ascii(b,start,4)==='MPF\0'))||
            (marker===238&&ascii(b,start,5)==='Adobe');
        if((app&&!keep)||marker===254){
            const exif=marker===225&&ascii(b,start,6)==='Exif\0\0'?cleanExif(b.subarray(start,end),true):null;
            out.fill(0,start,end);
            if(exif&&exif.length<=end-start)out.set(exif,start);
            else out[markerAt]=254; // Same-size empty comment preserves MPF absolute offsets.
        }
        p=end;
        if(marker===218){
            while(p<b.length){
                if(b[p]!==255){p++;continue;}
                let next=p+1;while(b[next]===255)next++;
                if(next>=b.length)throw invalid();
                if(b[next]===0||(b[next]>=208&&b[next]<=215)){p=next+1;continue;}
                break;
            }
        }
    }
    if(!ended||inside)throw invalid();return out;
}
function crc32(b:Uint8Array):number {let crc=0xffffffff;for(const byte of b){crc^=byte;for(let i=0;i<8;i++)crc=(crc>>>1)^((crc&1)?0xedb88320:0);}return (crc^0xffffffff)>>>0;}
function pngChunk(type:string,data:Uint8Array):Uint8Array {
    const b=new Uint8Array(data.length+12),v=new DataView(b.buffer);v.setUint32(0,data.length);
    b.set(Array.from(type,c=>c.charCodeAt(0)),4);b.set(data,8);v.setUint32(data.length+8,crc32(b.subarray(4,data.length+8)));return b;
}
function png(b:Uint8Array):Uint8Array {
    const parts=[b.subarray(0,8)],v=new DataView(b.buffer,b.byteOffset,b.byteLength);
    const visual=new Set(['tRNS','cHRM','gAMA','iCCP','sRGB','sBIT','pHYs','bKGD','acTL','fcTL','fdAT','cICP','mDCV','cLLI']);
    let p=8,ended=false;
    while(p<b.length){
        if(p+12>b.length)throw invalid();
        const length=v.getUint32(p),end=p+length+12;if(end>b.length)throw invalid();
        const type=ascii(b,p+4,4);
        if(type==='eXIf'){const exif=cleanExif(b.subarray(p+8,end-4),false);if(exif)parts.push(pngChunk(type,exif));}
        else if((b[p+4]&32)===0||visual.has(type))parts.push(b.subarray(p,end));
        p=end;if(type==='IEND'){ended=true;break;}
    }
    if(!ended)throw invalid();return join(parts);
}
function webp(b:Uint8Array):Uint8Array {
    const v=new DataView(b.buffer,b.byteOffset,b.byteLength),end=v.getUint32(4,true)+8;
    if(end>b.length||end<12)throw invalid();
    const parts:Uint8Array[]=[],visual=new Set(['VP8 ','VP8L','VP8X','ALPH','ANIM','ANMF','ICCP']);
    let p=12,hasExif=false;
    while(p<end){
        if(p+8>end)throw invalid();
        const type=ascii(b,p,4),length=v.getUint32(p+4,true),next=p+8+length+(length&1);
        if(next>end)throw invalid();
        if(type==='EXIF'){
            const payload=b.subarray(p+8,p+8+length),exif=cleanExif(payload,ascii(payload,0,6)==='Exif\0\0');
            if(exif){const chunk=new Uint8Array(8+exif.length+(exif.length&1));chunk.set(b.subarray(p,p+4));new DataView(chunk.buffer).setUint32(4,exif.length,true);chunk.set(exif,8);parts.push(chunk);hasExif=true;}
        }else if(visual.has(type))parts.push(b.slice(p,next));
        p=next;
    }
    for(const part of parts)if(ascii(part,0,4)==='VP8X'){if(part.length<18)throw invalid();part[8]=(part[8]&~12)|(hasExif?8:0);}
    const out=join([b.slice(0,12),...parts]);new DataView(out.buffer).setUint32(4,out.length-8,true);return out;
}
function gif(b:Uint8Array):Uint8Array {
    if(b.length<13)throw invalid();
    let p=13+((b[10]&128)?3*(1<<((b[10]&7)+1)):0);
    if(p>b.length)throw invalid();const parts=[b.subarray(0,p)];
    const blocks=(start:number)=>{let q=start;while(q<b.length){const n=b[q++];if(!n)return q;if(q+n>b.length)throw invalid();q+=n;}throw invalid();};
    while(p<b.length){
        const start=p;
        if(b[p]===59){parts.push(b.subarray(p,p+1));return join(parts);}
        if(b[p]===44){
            if(p+10>b.length)throw invalid();const packed=b[p+9];p+=10+((packed&128)?3*(1<<((packed&7)+1)):0);
            if(p>=b.length)throw invalid();p=blocks(p+1);parts.push(b.subarray(start,p));
        }else if(b[p]===33){
            if(p+2>=b.length)throw invalid();const label=b[p+1],name=b[p+2]===11?ascii(b,p+3,11):'';
            p=blocks(p+2);
            if(label===249||label===1||(label===255&&['NETSCAPE2.0','ANIMEXTS1.0','ICCRGBG1012'].includes(name)))parts.push(b.subarray(start,p));
        }else throw invalid();
    }
    throw invalid();
}
export function stripImageMetadata(bytes:Uint8Array):Uint8Array {
    if(bytes[0]===255&&bytes[1]===216)return jpeg(bytes);
    if(bytes.length>=8&&ascii(bytes,0,8)==='\x89PNG\r\n\x1a\n')return png(bytes);
    if(bytes.length>=12&&ascii(bytes,0,4)==='RIFF'&&ascii(bytes,8,4)==='WEBP')return webp(bytes);
    if(ascii(bytes,0,6)==='GIF87a'||ascii(bytes,0,6)==='GIF89a')return gif(bytes);
    throw invalid();
}
export async function preparePrivateImage(file:File):Promise<File> {
    // Media forms also carry videos; do not change their existing validation/encoding.
    if(!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type))return file;
    const data=typeof file.arrayBuffer==='function'?await file.arrayBuffer():await new Promise<ArrayBuffer>((resolve,reject)=>{
        const reader=new FileReader();reader.onload=()=>resolve(reader.result as ArrayBuffer);reader.onerror=()=>reject(invalid());reader.readAsArrayBuffer(file);
    });
    const clean=stripImageMetadata(new Uint8Array(data));
    return new File([clean],file.name,{type:file.type,lastModified:file.lastModified});
}
